import {
  FeedItemDto,
  MemberTransferRequestDto,
  UserMemberStatusDto,
} from '@act/shared/data-transfer-objects';
import { CircularProgress, Button, Typography } from '@mui/material';
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  EventItem,
  feedSelectors,
  loadFeedItems,
  selectFeedLoadingStatus,
  selectSelectedFeedFilters,
} from 'redux/slices/feedSlice';
import {
  getMemberStatuses,
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { selectedMemberSelectors } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { LoadingStatus } from 'redux/types/shared-types';
import AuditTransferRequestDialog from '../../dialog-forms/AuditTransferRequestDialog';
import FeedFilters from './feed-filters';
import { FilterDiv, ScrollableStyled } from './feed-styles';
import SingleFeedItem from './single-feed-item';
import MemberStatusEvent from './member-status-event';
import { FeedFilter } from 'redux/types/feedTypes';
import StyledAlert from 'toolkit/alerts/styled-alert';
import {
  getScheduledMessagesByMember,
  selectScheduledMessagesByMember,
} from 'redux/slices/messagesSlice';
import { ClockIcon } from '@mui/x-date-pickers/icons';
import ScheduledMessageDrawer from 'react-components/member-page/messaging-drawer/ScheduledMessageDrawer';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { useTracking } from 'react-components/tracking';
import { FlexContainer } from '@act/storybook';
import { useMobile } from 'utilities/mobile-context';

const deriveKey = (eventItem: EventItem) => {
  if (eventItem.type === 'feedItem') {
    const item = eventItem.item as FeedItemDto;
    return `feedItem-${item.externalItemType}-${item.externalItemId}-${item.id}`;
  } else {
    const item = eventItem.item as UserMemberStatusDto;
    return 'memberStatus' + item.id.toString();
  }
};

const ScrollableFeedItems = () => {
  const scheduledSmsFF = useFeatureFlag('scheduled-sms');
  const { generateTrackingProps } = useTracking();
  const selectedMember = useAppSelector(selectSelectedMember);
  const selectedMemberId = selectedMemberSelectors.useSelectSelectedMemberId();
  const memberStatuses = membersAppSelectors.useSelectAllMemberStatuses();
  const eventItems = feedSelectors.useSelectFeedItemsAndUserMemberStatuses();
  const feedLoading: LoadingStatus = useAppSelector(selectFeedLoadingStatus);
  const dispatch = useAppDispatch();
  const [feedInitialized, setFeedInitialized] = useState<boolean>(false);
  const [highlightedKey, setHighlightedKey] = useState<string>(null);
  const scrollElement = useRef<HTMLDivElement>(null);
  const [initialRefs, setInitialRefs] = useState<{
    [key in string]: React.RefObject<HTMLDivElement>;
  }>({});
  const [hasInitialData, setHasInitialData] = useState<boolean>(false);
  const [currentTransferRequest, setCurrentTransferRequest] =
    useState<MemberTransferRequestDto>(null);
  const [openAuditTransferDialog, setOpenAuditTransferDialog] =
    useState<boolean>(false);
  const { isMobile } = useMobile();

  const activeFilters = useAppSelector(selectSelectedFeedFilters);

  const [searchParams] = useSearchParams();
  const [feedItemExternalId] = searchParams.getAll('feedItemExternalId');
  const [feedItemExternalType] = searchParams.getAll('feedItemExternalType');
  const [scheduledMessageOpen, setScheduledMessageOpen] = useState(false);
  const selectedMessages = useAppSelector(
    selectScheduledMessagesByMember(selectedMemberId)
  );

  useEffect(() => {
    if (selectedMemberId) {
      dispatch(
        getScheduledMessagesByMember({
          params: { memberId: selectedMemberId },
        })
      );
    }
  }, [selectedMemberId]);

  function handleOpenTransferDialog(
    memberTransferRequest: MemberTransferRequestDto
  ) {
    setCurrentTransferRequest(memberTransferRequest);
    setOpenAuditTransferDialog(true);
  }

  function handleCloseTransferDialog() {
    setCurrentTransferRequest(null);
    setOpenAuditTransferDialog(false);
  }

  const eventItemMap = useMemo(() => {
    const map: { [key: string]: EventItem } = {};
    eventItems.forEach(item => {
      if (item.type === 'feedItem') {
        map[`${item.item.externalItemType}-${item.item.externalItemId}`] = item;
        map[item.item.id] = item;
      }
    });
    return map;
  }, [eventItems]);

  useEffect(() => {
    if (!memberStatuses.length) {
      dispatch(getMemberStatuses({}));
    }
  }, [memberStatuses.length]);

  useEffect(() => {
    if (!selectedMemberId) return;

    // Reset initialization state when member changes
    setHasInitialData(false);
    setFeedInitialized(false);
    setInitialRefs({});

    // Redux will deduplicate multiple dispatches for the same member
    dispatch(loadFeedItems({}));
  }, [selectedMemberId]);

  const handleScroll = useCallback(
    (e: Event) => {
      const element = e.target as HTMLDivElement;
      const isFeedLoading =
        feedLoading === 'loading' || feedLoading === 'initial';
      // element.clientHeight + 1 to account for half pixels
      if (
        element.scrollHeight - element.scrollTop <= element.clientHeight + 1 &&
        !isFeedLoading
      ) {
        dispatch(loadFeedItems({ earlier: true }));
      }
    },
    [feedLoading]
  );

  useEffect(() => {
    if (scrollElement.current) {
      scrollElement.current.addEventListener('scroll', handleScroll);
    }
    return () =>
      scrollElement.current?.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Force layout recalculation when mobile context changes (e.g., iPad split-screen)
  // This ensures the feed height adjusts properly when switching between desktop and mobile views
  useEffect(() => {
    if (scrollElement.current && hasInitialData) {
      // Trigger a reflow by accessing offsetHeight, then let the browser recalculate layout
      void scrollElement.current.offsetHeight;

      // Small delay to ensure parent containers have finished their layout changes
      const timeoutId = setTimeout(() => {
        if (scrollElement.current) {
          // Force the browser to recalculate scroll container dimensions
          scrollElement.current.style.display = 'none';
          void scrollElement.current.offsetHeight; // Force reflow
          scrollElement.current.style.display = '';
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isMobile, hasInitialData]);

  useEffect(() => {
    // Wait for feed loading to complete before showing content
    // This prevents flickering when member statuses load before feed items
    // (e.g., when navigating from docket where statuses are already cached)
    if (feedLoading === 'success' && eventItems.length > 0 && !hasInitialData) {
      setHasInitialData(true);

      // Incrementally add refs for new items instead of recreating all refs
      setInitialRefs(prevRefs => {
        const newRefs = { ...prevRefs };
        let hasNewRefs = false;

        eventItems.forEach(fi => {
          const key = deriveKey(fi);
          if (!newRefs[key]) {
            newRefs[key] = React.createRef();
            hasNewRefs = true;
          }
        });

        // Only update state if we actually added new refs
        return hasNewRefs ? newRefs : prevRefs;
      });
    }
  }, [feedLoading, eventItems]);

  useEffect(() => {
    if (hasInitialData && Object.keys(initialRefs).length > 0) {
      let key = null;
      if (feedItemExternalType && feedItemExternalId) {
        const foundEventItem =
          eventItemMap[`${feedItemExternalType}-${feedItemExternalId}`];
        if (foundEventItem) {
          key = deriveKey(foundEventItem);
        }
      }
      if (key) {
        setTimeout(() => {
          if (!feedInitialized) {
            initialRefs[key]?.current?.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => {
              setHighlightedKey(key);
            }, 1000);
            setTimeout(() => {
              setHighlightedKey(null);
            }, 3500);
          }
        }, 200);
      }

      setFeedInitialized(true);
    }
  }, [
    hasInitialData,
    initialRefs,
    feedItemExternalId,
    feedItemExternalType,
    eventItemMap,
  ]);

  const scrollToEventFeedItem = useCallback(
    (id: string | number) => {
      const foundEventItem = eventItemMap[id];
      const key = foundEventItem ? deriveKey(foundEventItem) : null;

      if (initialRefs[key]) {
        setHighlightedKey(key);
        initialRefs[key]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
        setTimeout(() => {
          setHighlightedKey(null);
        }, 3500);
      }
    },
    [eventItemMap, initialRefs]
  );

  // Memoize additional dialogs to prevent breaking SingleFeedItem memoization
  const emptyDialogs = useMemo(() => ({}), []);
  const additionalDialogsForTransfer = useMemo(
    () => ({
      openTransferDialog: handleOpenTransferDialog as (
        ...args: unknown[]
      ) => void,
    }),
    []
  );

  const getAdditionalDialogs = useCallback(
    (
      feedItemType: string
    ): {
      [key: string]: (...args: unknown[]) => void;
    } => {
      switch (feedItemType) {
        case 'member_transfer_request':
          return additionalDialogsForTransfer;
        default:
          return emptyDialogs;
      }
    },
    [additionalDialogsForTransfer, emptyDialogs]
  );

  // Memoize filtered event items to prevent recalculation on every render
  const filteredEventItems = useMemo(() => {
    return eventItems.filter(fi => {
      // If there are no active filters, show all items
      if (activeFilters.length === 0) {
        return true;
      }

      // For member status events, only show when no filters are active
      if (fi.type === 'userMemberStatus') {
        return false;
      }

      // For feed items, filter based on their external item type
      if (fi.type === 'feedItem') {
        const item = fi.item as FeedItemDto;

        // Handle special case mappings
        if (
          activeFilters.includes('hospital events') &&
          item.externalItemType === 'hospitalization'
        ) {
          return true;
        }

        if (activeFilters.includes('task')) {
          const taskTypes = [
            'member_status_change_request',
            'member_transfer_request',
            'assigned_workflow_phase',
          ];
          if (taskTypes.includes(item.externalItemType)) {
            return true;
          }
        }

        // Direct mapping for other filter types
        return activeFilters.includes(item.externalItemType as FeedFilter);
      }

      return false;
    });
  }, [eventItems, activeFilters]);

  if (!hasInitialData) {
    return (
      <FlexContainer
        direction="row"
        alignItems="center"
        justifyContent="center"
        height="fit-content"
        width="100%"
        padding="24px 0"
      >
        <CircularProgress />
      </FlexContainer>
    );
  } else {
    return (
      <>
        <ScrollableStyled ref={scrollElement}>
          <div className="scroll-content">
            <FilterDiv>
              <FeedFilters />
            </FilterDiv>
            {scheduledSmsFF && selectedMessages?.length > 0 && (
              <StyledAlert
                mode="inline"
                severity="info"
                message={` ${selectedMessages?.length} ${
                  selectedMessages?.length === 1 ? 'text' : 'texts'
                } scheduled`}
                icon={<ClockIcon style={{ height: '20px', width: '20px' }} />}
                actions={
                  <Button
                    onClick={() => setScheduledMessageOpen(true)}
                    sx={{
                      padding: '4px',
                    }}
                    {...generateTrackingProps('open-scheduled-messages-drawer')}
                  >
                    <Typography variant="bodyMedium">View</Typography>
                  </Button>
                }
              />
            )}
            {!!selectedMember?.phoneNumbers?.find(
              phoneNumber =>
                !!phoneNumber.doNotContact &&
                phoneNumber.doNotContact?.reason === '"STOP" message'
            ) && (
              <StyledAlert
                message={`This Member texted "STOP" for do not contact. Instruct member to text "START" to opt back in and receive text messages.`}
                severity="info"
                variant="filled"
              />
            )}

            {filteredEventItems.map((fi, index) => {
              const key = deriveKey(fi);
              const partnerKey = `${fi.item.externalItemType}-${fi.item.externalItemId}`;

              if (fi.type === 'userMemberStatus') {
                const item = fi.item as UserMemberStatusDto;
                return (
                  <MemberStatusEvent
                    key={key}
                    userMemberStatus={item}
                    showJumpButton={!!eventItemMap[partnerKey]}
                    previousItem={filteredEventItems[index - 1]}
                    scrollToEventFeedItem={scrollToEventFeedItem}
                  />
                );
              }

              if (fi.type === 'feedItem') {
                const item = fi.item as FeedItemDto;
                const previousTimestamp =
                  filteredEventItems[index - 1]?.timestamp || 0;
                return (
                  <SingleFeedItem
                    key={key}
                    ref={initialRefs[key]}
                    feedItem={item}
                    previousTimestamp={previousTimestamp}
                    isLastEventItem={index === 0}
                    shouldHighlight={key === highlightedKey}
                    additionalDialogs={getAdditionalDialogs(
                      item.externalItemType
                    )}
                  ></SingleFeedItem>
                );
              }
            })}
            <AuditTransferRequestDialog
              requestMember={selectedMember}
              transferRequest={currentTransferRequest}
              open={openAuditTransferDialog}
              handleClose={handleCloseTransferDialog}
            />
          </div>
        </ScrollableStyled>
        {scheduledSmsFF && (
          <ScheduledMessageDrawer
            selectedMember={selectedMember}
            open={scheduledMessageOpen}
            onClose={() => setScheduledMessageOpen(false)}
            scheduledMessages={selectedMessages
              ?.filter(message => message.sendAt != null)
              ?.sort((a, b) => a.sendAt - b.sendAt)}
          />
        )}
      </>
    );
  }
};

export default ScrollableFeedItems;
